import { initializeApp, FirebaseApp } from 'firebase/app'
import { getMessaging, getToken, onMessage, deleteToken, Messaging } from 'firebase/messaging'

// evertel
import { decorate, inject, injectable } from '@evertel/di'
import { NotificationPermission, NotificationPermissionResult, PushImplementation, GPCPushNotification, ScheduleNotificationOptions, parsePushNotification } from '@evertel/push'
import { ToastService } from '@evertel/web/ui'
import { isEdgeBrowser } from '@evertel/web/utils'

import debugModule from 'debug'
const debugLog = debugModule('app:Push:WebPushImplementation')

const PROMPT_TIMEOUT_MS = 500


/**
 * WebPushImplementation class
 *
 * This class implements the PushImplementation interface for web browsers,
 * handling push notifications using Firebase Cloud Messaging (FCM).
 */
class WebPushImplementation implements PushImplementation {
    public pendingNotification: GPCPushNotification | undefined = undefined
    private firebaseApp!: FirebaseApp
    private messaging!: Messaging
    private token = ''
    public onMessageCallback: ((notification: GPCPushNotification) => void) | undefined
    public onPermissionChangeCallback: ((permission: NotificationPermission) => void) | undefined
    public onNotificationClickedCallback: ((notification: GPCPushNotification) => void) | undefined

    constructor(private toastService: ToastService) { }


    /**
     * Initialize the WebPushImplementation
     * This method sets up Firebase, registers the service worker, and sets up message handlers
     */
    async init(): Promise<void> {
        debugLog('Initializing WebPushImplementation')

        // Firebase configuration
        const firebaseConfig = {
            apiKey: 'AIzaSyBd6D1557vYaoxtWiVc0jbByXxSl8dYGfU',
            authDomain: 'blue-12d4a.firebaseapp.com',
            databaseURL: 'https://blue-12d4a.firebaseio.com',
            projectId: 'blue-12d4a',
            storageBucket: 'blue-12d4a.appspot.com',
            messagingSenderId: '311541489188',
            appId: '1:311541489188:web:8e8560f708b2c6ea1f42b1'
        }

        // Initialize Firebase
        this.firebaseApp = initializeApp(firebaseConfig)
        this.messaging = getMessaging(this.firebaseApp)

        // Register service worker
        // This is registered by firebase and will result in duplicate registrations if activated
        // await this.registerServiceWorker()

        // Set up message handler
        this.setupMessageHandler()

        // Set up permission change listener
        this.setupPermissionChangeListener()
    }

    /**
     * Register the Firebase messaging service worker
     */
    private async registerServiceWorker(): Promise<void> {
        if ('serviceWorker' in navigator) {
            try {
                const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js')
                debugLog('Service Worker registered with scope:', registration.scope)
            } catch (error) {
                debugLog('Service Worker registration failed:', error)
                throw error
            }
        } else {
            console.warn('app:Push:WebPushImplementation: Service workers are not supported in this browser')
        }
    }

    /**
     * Set up the message handler for incoming push notifications
     */
    private setupMessageHandler(): void {
        onMessage(this.messaging, (payload) => {
            debugLog('Received foreground message:', payload)
            const pushNotification = parsePushNotification(payload)

            if (pushNotification) {
                this.handleForegroundNotification(pushNotification)
            } else {
                console.error('app:Push:WebPushImplementation: Failed to parse push notification:', payload)
            }
        })

        // Listen for messages from the service worker
        navigator.serviceWorker.addEventListener('message', (event) => {
            if (!event.data) return

            debugLog('Received message event type:', event.data.type, event.data)
            const pushNotification = parsePushNotification(event.data, false)

            switch (event.data.type) {
                case 'BACKGROUND_MESSAGE': {

                    if (pushNotification) {
                        this.handleBackgroundNotification(pushNotification)
                    }
                    return
                }
                case 'NAVIGATE': {
                    if (pushNotification) {
                        if (this.onNotificationClickedCallback) {
                            this.onNotificationClickedCallback(pushNotification)
                        }
                    }

                    return
                }
                default:
                    debugLog('Received message event:', event.data)
            }
        })
    }

    /**
     * Handle a foreground notification
     * @param pushNotification The parsed push notification
     */
    private handleForegroundNotification(pushNotification: GPCPushNotification): void {
        const currentPath = window.location.pathname // Remove the leading '#'
        const notificationPath = this.getNotificationPath(pushNotification)

        // Call the message callback if set
        if (this.onMessageCallback) {
            this.onMessageCallback(pushNotification)
        }

        // Don't show notification if user is already in the relevant room/thread
        if (pushNotification.notification) {
            if (currentPath !== notificationPath) {
                this.showToast(pushNotification)
                this.playNotificationSound(pushNotification)
            }
        }

    }

    /**
     * Handle a background notification
     * @param pushNotification The parsed push notification
     */
    private handleBackgroundNotification(pushNotification: GPCPushNotification): void {
        // Play notification sound
        this.playNotificationSound(pushNotification)

        // Call the message callback if set
        if (this.onMessageCallback) {
            this.onMessageCallback(pushNotification)
        }
    }

    /**
     * Show a toast notification
     * @param pushNotification The push notification to display
     */
    private showToast(pushNotification: GPCPushNotification): void {
        const { type, repliesToId, isUrgent } = pushNotification.data
        let title = 'New Notification'

        if (pushNotification.notification?.title) {
            title = pushNotification.notification.title

            if (type === 'RoomMessage') {
                title = `#${title}`
                title += repliesToId ? ' - New Reply' : ''
            }

            if (isUrgent) {
                title = `Urgent: ${title}`
            }
        }

        const color = pushNotification.data.isUrgent ? 'danger' : 'info'

        this.toastService.addToast({
            onClick: () => {
                if (this.onNotificationClickedCallback) {
                    this.onNotificationClickedCallback(pushNotification)
                }
            },
            color: color,
            title: title,
            message: pushNotification.notification?.body
        })
    }

    /**
     * Get the path for the notification based on its type
     * @param pushNotification The push notification
     * @returns The path string
     */
    private getNotificationPath(pushNotification: GPCPushNotification): string {
        const { type, roomId, repliesToId, threadId, auditType } = pushNotification.data

        if (type === 'RoomMessage') return repliesToId ? `/room/${roomId}/replies/${repliesToId}` : `/room/${roomId}`
        if (type === 'ThreadMessage') return `/thread/${threadId}`
        if (type === 'AuditComplete') return auditType === 'room_export' ? `/room/${roomId}` : `/manage/audits`
        return '/' // Default path if type is unknown
    }

    /**
     * Play the notification sound
     * @param pushNotification The push notification
     */
    private playNotificationSound(pushNotification: GPCPushNotification): void {
        const soundFile = `/assets/audio/notifications/` + (pushNotification.notification?.sound ? (pushNotification.notification?.sound + '.mp3') : 'default.mp3')
        const audio = new Audio(soundFile)
        audio.play().catch(error => console.error('app:Push:WebPushImplementation: Failed to play notification sound:', error))
    }

    /**
     * Set up a listener for permission changes
     */
    private setupPermissionChangeListener(): void {
        if ('permissions' in navigator) {
            navigator.permissions.query({ name: 'notifications' }).then((permissionStatus) => {
                permissionStatus.onchange = () => {
                    const permission = this.mapPermissionStatus(permissionStatus.state)
                    if (this.onPermissionChangeCallback) {
                        this.onPermissionChangeCallback(permission)
                    }
                }
            })
        }
    }

    /**
     * Map the browser's permission status to our NotificationPermission type
     * @param status The browser's permission status
     * @returns The mapped NotificationPermission
     */
    private mapPermissionStatus(status: PermissionState): NotificationPermission {
        switch (status) {
            case 'granted': return 'granted'
            case 'denied': return 'denied'
            default: return 'default'
        }
    }

    /**
     * Prompt the user for notification permission
     * @returns A promise resolving to the notification permission result
     */
    async promptForNotificationPermission(): Promise<NotificationPermission> {
        if (!('Notification' in window)) {
            return 'default'
        }

        // For Edge browser, use timeout and fallback
        if (isEdgeBrowser(window.navigator.userAgent)) {
            try {
                return await new Promise<NotificationPermission>((resolve) => {
                    // Start the permission request
                    const permissionPromise = Notification.requestPermission()

                    // Set up timeout
                    const timeoutId = setTimeout(() => {
                        resolve(Notification.permission as NotificationPermission)
                    }, PROMPT_TIMEOUT_MS)

                    // Handle the permission promise
                    permissionPromise.then(
                        (result) => {
                            clearTimeout(timeoutId)
                            resolve(result as NotificationPermission)
                        },
                        () => {
                            clearTimeout(timeoutId)
                            resolve(Notification.permission as NotificationPermission)
                        }
                    )
                })
            } catch (error) {
                console.error('Permission request error:', error)
                return Notification.permission as NotificationPermission
            }
        }

        // For non-Edge browsers, use standard approach
        try {
            return await Notification.requestPermission() as NotificationPermission
        } catch (error) {
            console.error('Standard permission request error:', error)
            return Notification.permission as NotificationPermission
        }
    }

    /**
     * Get the current notification permission
     * @returns A promise resolving to the current notification permission
     */
    async getCurrentNotificationPermission(): NotificationPermissionResult {
        if ('Notification' in window) {
            return Notification.permission
        }
        return 'default'
    }

    /**
     * Get the FCM token for this device
     * @returns A promise resolving to the FCM token
     */
    async getPushToken(): Promise<string> {
        if (!this.token) {
            try {
                this.token = await getToken(this.messaging, {
                    vapidKey: 'BKulwf-fxOJ35dIWDPRJHPp1qXXy6h29wGrKtASpRQor9O8WExBX0MYXACqNkVHkLeKId0q6OLCnBocv_AObF-A'
                })
                // debug('FCM Token:', this.token)
            } catch (error) {
                console.error('app:Push:WebPushImplementation: Error getting FCM token:', error)
                throw error
            }
        }
        return this.token
    }

    /**
     * Delete the current FCM token
     */
    async deletePushToken(): Promise<void> {
        if (this.token) {
            try {
                await deleteToken(this.messaging)
                this.token = ''
                debugLog('FCM token deleted')
            } catch (error) {
                console.error('app:Push:WebPushImplementation: Error deleting FCM token:', error)
                throw error
            }
        }
    }

    scheduleNotification(options: ScheduleNotificationOptions): Promise<string | undefined> {
        throw new Error('Method not implemented.')
    }

    /**
     * Show a local notification
     * This is typically used to confirm that notifications are working
     */
    async showLocalNotification(): Promise<void> {
        if ('Notification' in window && Notification.permission === 'granted') {
            new Notification('Evertel Notifications', {
                body: 'Notifications are now enabled for this browser!',
                icon: 'https://www.evertelapp.com/assets/img/connect-logo-110x110.png'
            })
        }
    }

    /**
     * Set badge count
     * @param count The badge count to set
     */
    async setBadge(count: number): Promise<void> {
        // Web doesn't support setting badge counts in the same way as mobile
        debugLog('setBadge called with count:', count)
        // Some browsers support navigator.setAppBadge() but it's not widely supported
        if ('setAppBadge' in navigator && typeof (navigator as any).setAppBadge === 'function') {
            try {
                await (navigator as any).setAppBadge(count)
            } catch (error) {
                debugLog('Failed to set app badge:', error)
            }
        }
    }

    /**
     * Get displayed notifications
     * Note: Web browsers don't provide an API to list displayed notifications
     * @returns An empty array as web doesn't support listing displayed notifications
     */
    async getDisplayedNotifications(): Promise<GPCPushNotification[]> {
        debugLog('getDisplayedNotifications called - not supported on web')
        return []
    }

    /**
     * Cancel notifications by IDs
     * Note: Web browsers don't provide an API to cancel specific notifications by ID
     * @param notificationIds The notification IDs to cancel
     * @returns false as web doesn't support canceling notifications by ID
     */
    async cancelNotifications(notificationIds: string[]): Promise<boolean> {
        debugLog('cancelNotifications called with IDs:', notificationIds, '- not supported on web')
        return false
    }

    /**
     * Process pending notification
     * Not implemented for web as notifications are handled differently
     */
    processPendingNotification(): void {
        debugLog('processPendingNotification called - not implemented on web')
    }
}

// Apply the injectable decorator
decorate(injectable(), WebPushImplementation)
decorate(inject(ToastService), WebPushImplementation, 0)

export { WebPushImplementation }
